import React from 'react';
import FeatureFlag from '../../FeatureFlag';
import TrialSignupForm from '../TrialSignupForm';
import { useTrialOffer } from '@shared/ui/lib/useTrialOffer';

/**
 * The landing page's hero: the pitch on the left, the sign-up form on the
 * right. The register page is this same hero, so Join Now lands on the form
 * visitors already saw on the home page.
 */
const SignupHero: React.FC<{ source?: 'home' | 'register' }> = ({ source = 'home' }) => {
  const { trialDays } = useTrialOffer();
  return (
    <div className="bg-gradient-to-br from-deep-navy to-deep-navy-light text-white px-6 py-12 md:px-12">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

        {/* Left column — headline + features */}
        <div className="flex flex-col gap-6">
          <p className="text-racing-green-light text-sm font-semibold uppercase tracking-wider">
            Australia's Smartest Racing Platform
          </p>
          <h1 className="text-3xl md:text-5xl font-heading font-bold leading-tight">
            Data-driven tips for every race, every track.
          </h1>
          <p className="text-gray-300 text-lg max-w-xl">
            TippingIQ combines form analysis, speed maps, track bias, and market intelligence across Australian, New Zealand, and Hong Kong race tracks — giving you the edge before the gates open.
          </p>

          {/* Feature list */}
          <ul className="flex flex-col gap-3 list-none m-0 p-0">
            {[
              'Advanced analytics across all race venues',
              'Real-time updates and live race data',
              'Expert insights from industry professionals',
              'Proven results — no credit card required',
            ].map(text => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-racing-green flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-[15px] text-gray-300">{text}</span>
              </li>
            ))}
          </ul>

          <p className="text-gray-400 text-sm">
            {trialDays
              ? <>No credit card &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Full access from day one</>
              : <>Free account &nbsp;·&nbsp; No credit card to join &nbsp;·&nbsp; Cancel anytime</>}
          </p>
        </div>

        {/* Right column — signup form (first on a phone on the register page, where it is the point) */}
        <div className={source === 'register' ? 'order-first lg:order-none' : undefined}>
          <FeatureFlag
            feature="enableRegistration"
            fallback={
              <a href="#features" className="inline-block bg-race-gold text-deep-navy px-6 py-3 rounded-md font-bold hover:bg-race-gold-dark transition-colors">
                Explore Our Features
              </a>
            }
          >
            <TrialSignupForm source={source} />
          </FeatureFlag>
        </div>
      </div>
    </div>
  );
};

export default SignupHero;
